import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, todayISO } from "../lib/api";
import { PageHeader } from "../components/PageHeader";

const inputCls = "h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm outline-none focus:border-slate-900 transition-colors";

export default function Holidays() {
  const [year, setYear] = useState(Number(todayISO().slice(0, 4)));
  const [list, setList] = useState([]);
  const [date, setDate] = useState(todayISO());
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/holidays", { params: { year } }).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e))), [year]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/holidays", { date, name }); toast.success("Holiday added"); setName(""); load(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const remove = async (h) => {
    if (!window.confirm(`Remove holiday "${h.name}" on ${h.date}?`)) return;
    try { await api.delete(`/holidays/${h.id}`); toast.success("Holiday removed"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const today = todayISO();
  return (
    <>
      <PageHeader overline={`${list.length} holidays in ${year}`} title="Holidays Calendar">
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="holidays-year-select" className={inputCls}>
          {[year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </PageHeader>

      <form onSubmit={add} className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col sm:flex-row gap-3 sm:items-end">
        <label className="space-y-1"><span className="block text-xs font-medium text-slate-600">Date</span>
          <input type="date" required value={date} onChange={(e) => setDate(e.target.value)} data-testid="holiday-date-input" className={inputCls} />
        </label>
        <label className="space-y-1 flex-1"><span className="block text-xs font-medium text-slate-600">Holiday name</span>
          <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Independence Day" data-testid="holiday-name-input" className={`${inputCls} w-full`} />
        </label>
        <button disabled={busy} data-testid="holiday-add-btn" className="h-10 px-5 rounded-full bg-slate-900 hover:bg-emerald-500 hover:text-slate-950 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition-colors">
          <Plus size={16} /> Add holiday
        </button>
      </form>
      <p className="text-sm text-slate-500 -mt-4">Nobody is marked absent on a holiday. Workers who still punch in are logged as "Holiday" (worked on off day).</p>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="holidays-list">
        {list.length === 0 && <p className="text-slate-400 text-sm">No holidays added for {year}.</p>}
        {list.map((h, i) => {
          const d = new Date(`${h.date}T00:00:00`);
          const past = h.date < today;
          return (
            <div key={h.id} data-testid={`holiday-card-${h.id}`} className={`rise flex items-center gap-4 bg-white border rounded-xl p-4 ${h.date === today ? "border-sky-400 ring-2 ring-sky-100" : "border-slate-200"} ${past ? "opacity-60" : ""}`} style={{ animationDelay: `${i * 30}ms` }}>
              <div className="w-14 h-14 rounded-lg bg-sky-50 border border-sky-200 grid place-items-center text-center shrink-0">
                <div><p className="font-mono text-[10px] uppercase text-sky-600">{d.toLocaleDateString([], { month: "short" })}</p><p className="font-mono text-xl font-bold text-sky-800 leading-none">{d.getDate()}</p></div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900 truncate flex items-center gap-2">{h.date === today && <PartyPopper size={14} className="text-sky-600" />}{h.name}</p>
                <p className="text-xs text-slate-500">{d.toLocaleDateString([], { weekday: "long" })}</p>
              </div>
              <button onClick={() => remove(h)} data-testid={`holiday-delete-btn-${h.id}`} className="p-2 rounded-md hover:bg-rose-100 text-rose-600 transition-colors"><Trash2 size={14} /></button>
            </div>
          );
        })}
      </div>
    </>
  );
}
